/**
 * useWaComposerHandlers — handlers del composer del inbox WhatsApp.
 *
 * Encapsula los 6 handlers que la página solía declarar inline:
 *   - sendText        → mensaje de texto + nota interna + @menciones
 *   - sendMedia       → upload de archivo + send (optimistic UI con URL local)
 *   - sendMediaUrl    → reuso de quick reply con media pre-subida
 *   - retryMessage    → re-enviar uno failed
 *   - scheduleMessage → programar para más tarde (`wa_scheduled_messages`)
 *   - forwardMessage  → reenviar a otra conversación
 *
 * Los 6 comparten el mismo patrón de optimistic UI (insertar tempId,
 * llamar edge function, marcar fail si error). Por eso conviene tenerlos
 * juntos en un único hook que la página orquesta.
 */

import { useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { enviarTexto } from '@/lib/canales/enviarTexto';
import { uploadAsset } from '@/lib/storage';
import { traceSpan } from '@/lib/trace';
import { logError } from '@/lib/logger';
import { devLog } from '@/lib/utils';
import { playSendSound } from '@/lib/whatsappSounds';
import { waAuditLog } from '@/lib/waAudit';
import { newIdempotencyKey } from '@/lib/waIdempotency';
import { useAdminToast } from '@/contexts/AdminToastContext';
import type { WaMessage } from '@/components/whatsapp/WaMessageThread';
import type { WaConversation } from '@/hooks/useWaConversations';

interface Options {
  tenantId: string | null;
  userId: string | undefined;
  selectedConvId: string | null;
  messages: WaMessage[];
  setMessages: React.Dispatch<React.SetStateAction<WaMessage[]>>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  replyingTo: WaMessage | null;
  setReplyingTo: (msg: WaMessage | null) => void;
  /**
   * Setter de la lista de chats. Se usa para optimistic update del
   * `last_message_*` y reordenamiento al top cuando el agente envía.
   * Sin esto la lista quedaba con el body anterior hasta que el
   * realtime trajera el INSERT/UPDATE (1-3s de delay visible).
   */
  setConversations?: React.Dispatch<React.SetStateAction<WaConversation[]>>;
  onForwardComplete?: () => void;
}

/** Aplica patch al last_message_* de una conv y la mueve al top. */
function bumpConversationToTop(
  setConversations: React.Dispatch<React.SetStateAction<WaConversation[]>>,
  convId: string,
  patch: Partial<WaConversation>,
): void {
  setConversations((prev) => {
    const patched = prev.map((c) => (c.id === convId ? { ...c, ...patch } : c));
    return [...patched].sort((a, b) => {
      const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return tb - ta;
    });
  });
}

type MediaType = 'image' | 'video' | 'document' | 'audio';

/**
 * Tipos que se reenvían como ARCHIVO (no como texto). Incluye 'voice' y
 * 'sticker', que antes caían al branch de texto y se perdían.
 */
const FORWARDABLE_MEDIA_TYPES = new Set(['image', 'video', 'document', 'audio', 'voice', 'sticker']);

/**
 * Materializa la URL de un media lazy (mismo endpoint que usa el visor,
 * `useWaLazyMedia`). Devuelve null si no se pudo resolver.
 */
async function resolveMediaUrl(messageId: string, accessToken: string): Promise<string | null> {
  try {
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-fetch-media`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ message_id: messageId }),
    });
    if (!res.ok) return null;
    const { url } = (await res.json()) as { url?: string };
    return url ?? null;
  } catch {
    return null;
  }
}

const SCROLL_DELAY_MS = 30;

function tempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function scheduleScrollToBottom(ref: React.RefObject<HTMLDivElement | null>): void {
  setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth' }), SCROLL_DELAY_MS);
}

export function useWaComposerHandlers({
  tenantId,
  userId,
  selectedConvId,
  messages,
  setMessages,
  messagesEndRef,
  replyingTo,
  setReplyingTo,
  setConversations,
  onForwardComplete,
}: Options) {
  const toast = useAdminToast();

  // ─── Texto + nota interna + @menciones ───
  const sendText = useCallback((text: string, sendingNote: boolean, mentionedIds?: string[]) => {
    if (!selectedConvId || !text.trim()) return;
    const id = tempId();
    const quotedMsg = replyingTo;
    setReplyingTo(null);

    if (!sendingNote) playSendSound();

    setMessages((prev) => [...prev, {
      id,
      direction: 'outbound',
      sender_type: 'agent',
      sender_id: null,
      body: text,
      msg_type: 'text',
      media_url: null,
      media_caption: null,
      media_filename: null,
      media_status: null,
      delivery_status: sendingNote ? 'delivered' : 'sent',
      is_internal_note: sendingNote,
      created_at: new Date().toISOString(),
      reactions: [],
      wa_message_id: null,
      ...(quotedMsg ? { quoted_message_id: quotedMsg.id, quoted_message: quotedMsg } : {}),
    }]);
    scheduleScrollToBottom(messagesEndRef);

    // Optimistic en la lista: bump al top + preview del texto. Sin esto
    // la lista no se actualizaba hasta que llegara el realtime (1-3s).
    if (setConversations && !sendingNote) {
      bumpConversationToTop(setConversations, selectedConvId, {
        last_message_body: text.slice(0, 200),
        last_message_type: 'text',
        last_message_direction: 'outbound',
        last_message_status: 'sent',
        last_message_at: new Date().toISOString(),
      });
    }

    const convId = selectedConvId;
    const authorId = userId;
    const idempotencyKey = newIdempotencyKey();
    void traceSpan('wa.send', {
      tenant_id: tenantId ?? '',
      user_id: authorId,
      conversation_id: convId,
      msg_type: 'text',
      is_internal_note: !!sendingNote,
    // 🪤 ENRUTADO POR CANAL, NO `whatsapp-send-message` A SECAS. La bandeja ya
    // tiene conversaciones del chat del sitio y preguntas de Mercado Libre, y
    // esa función rechaza con 409 todo lo que no sea WhatsApp (ADR 0023): el
    // agente veía la conversación, escribía la respuesta y le rebotaba.
    // Ver `@/lib/canales/enviarTexto`.
    }, () => enviarTexto({
      conversationId: convId,
      texto: text,
      esNotaInterna: !!sendingNote,
      claveIdempotencia: idempotencyKey,
      citandoMensajeId: quotedMsg?.wa_message_id ?? null,
      mencionados: mentionedIds,
    }).then((r) => ({
      // Se adapta al shape que ya esperaba el `.then` de abajo, para no tocar
      // el manejo de errores que ya funcionaba.
      error: r.ok ? null : { message: r.error ?? 'No se pudo enviar' },
      data: r.ok ? {} : { error: r.error },
    }))).then(({ error, data }) => {
      if (error) {
        const errMsg = (data as Record<string, unknown>)?.error as string | undefined;
        setMessages((prev) => prev.map((m) =>
          m.id === id ? { ...m, delivery_status: 'failed', error_message: errMsg ?? error.message } : m,
        ));
        return;
      }
      // Indexar @menciones de notas internas en wa_message_mentions
      // (post-success, client-side — el caso típico es 1-3 menciones).
      if (sendingNote && mentionedIds && mentionedIds.length > 0 && tenantId && authorId) {
        const realMsgId = (data as Record<string, unknown>)?.message_id as string | undefined;
        if (!realMsgId) return;
        const rows = mentionedIds.map((uid) => ({
          tenant_id: tenantId,
          conversation_id: convId,
          message_id: realMsgId,
          mentioned_user_id: uid,
          mentioned_by_user_id: authorId,
        }));
        void supabase.from('wa_message_mentions').insert(rows).then(({ error: mErr }) => {
          if (mErr) logError('Insert wa_message_mentions falló', mErr);
        });
      }
    });
  }, [selectedConvId, replyingTo, setReplyingTo, setMessages, messagesEndRef, tenantId, userId, setConversations]);

  // ─── Media file (upload + send) ───
  // `isNote`: adjuntar en modo "Nota interna" NO puede salir al cliente. Antes el
  // flag no viajaba y el archivo se enviaba por WhatsApp aunque el composer
  // mostrara el cartel amarillo de nota → fuga de datos (capturas internas, pagos).
  const sendMedia = useCallback(async (file: File, type: MediaType, audioDuration?: number, isNote?: boolean, caption?: string) => {
    if (!selectedConvId || !tenantId) return;
    playSendSound();

    // Optimistic UI con URL local — el audio se reproduce sin esperar upload.
    const localUrl = URL.createObjectURL(file);
    const id = tempId();
    setMessages((prev) => [...prev, {
      id, direction: 'outbound', sender_type: 'agent', sender_id: null,
      body: null, msg_type: type, media_url: localUrl, media_caption: caption ?? null,
      // filename real del File: los documentos optimistas ahora muestran su
      // nombre en el bubble (antes quedaba undefined hasta el realtime).
      // media_status null (no 'pending'): la blob local ya es reproducible.
      media_filename: file.name, media_status: null,
      delivery_status: 'sent', is_internal_note: !!isNote, created_at: new Date().toISOString(),
      reactions: [], wa_message_id: null,
      ...(type === 'audio' && audioDuration ? { audio_duration: audioDuration } : {}),
    }]);
    scheduleScrollToBottom(messagesEndRef);

    // Optimistic en la lista: bump al top con preview del tipo de media.
    // Una nota interna NO es un mensaje del chat: no debe pisar el preview.
    if (setConversations && !isNote) {
      const icon = type === 'audio' ? '🎤' : type === 'image' ? '📷' : type === 'video' ? '🎥' : '📎';
      const label = type === 'audio' ? 'Audio' : type === 'image' ? 'Foto' : type === 'video' ? 'Video' : 'Documento';
      // Con pie de foto, el preview muestra el texto (como WhatsApp).
      const preview = `${icon} ${caption?.trim() || label}`;
      bumpConversationToTop(setConversations, selectedConvId, {
        last_message_body: preview,
        last_message_type: type,
        last_message_direction: 'outbound',
        last_message_status: 'sent',
        last_message_at: new Date().toISOString(),
      });
    }

    // Upload a R2 vía presigned PUT (background mientras user ya ve el mensaje).
    let publicUrl: string;
    try {
      const uploaded = await uploadAsset(file, {
        category: 'messages',
        identifier: String(Date.now()),
        tenantId: tenantId ?? undefined,
        allowedTypes: [],            // el picker ya restringió el tipo; el server valida media WA
        maxBytes: 64 * 1024 * 1024,  // paridad con storage-presign (video/documentos WA)
      });
      publicUrl = uploaded.url;
    } catch (uploadErr) {
      devLog.error('Error al subir archivo:', uploadErr instanceof Error ? uploadErr.message : String(uploadErr));
      setMessages((prev) => prev.map((m) => m.id === id ? { ...m, delivery_status: 'failed' } : m));
      URL.revokeObjectURL(localUrl);
      return;
    }
    setMessages((prev) => prev.map((m) => m.id === id ? { ...m, media_url: publicUrl } : m));
    // Revocar la blob *después* de un tick: si revocamos sync, el <img>/<video>
    // puede aún estar leyendo de la blob y muestra "loading roto" → "no
    // disponible". Esperar al próximo paint da tiempo al swap de src.
    setTimeout(() => URL.revokeObjectURL(localUrl), 100);

    const session = (await supabase.auth.getSession()).data.session;
    const { error } = await supabase.functions.invoke('whatsapp-send-message', {
      body: {
        conversation_id: selectedConvId,
        msg_type: type,
        media_url: publicUrl,
        media_filename: file.name,
        client_idempotency_key: newIdempotencyKey(),
        ...(caption ? { media_caption: caption } : {}),
        ...(isNote ? { is_internal_note: true } : {}),
      },
      headers: { Authorization: `Bearer ${session?.access_token ?? ''}` },
    });
    if (error) {
      devLog.error('Error enviando media:', error.message);
      setMessages((prev) => prev.map((m) => m.id === id ? { ...m, delivery_status: 'failed' } : m));
    }
  }, [selectedConvId, tenantId, setMessages, messagesEndRef, setConversations]);

  // ─── Media por URL (quick reply con media pre-subida) ───
  const sendMediaUrl = useCallback(async (url: string, type: string, caption?: string) => {
    if (!selectedConvId || !tenantId) return;
    playSendSound();
    const id = tempId();
    setMessages((prev) => [...prev, {
      id, direction: 'outbound', sender_type: 'agent', sender_id: null,
      body: null, msg_type: type, media_url: url, media_caption: caption ?? null,
      media_filename: null, media_status: null,
      delivery_status: 'sent', is_internal_note: false, created_at: new Date().toISOString(),
      reactions: [], wa_message_id: null,
    }]);
    scheduleScrollToBottom(messagesEndRef);
    const session = (await supabase.auth.getSession()).data.session;
    const { error } = await supabase.functions.invoke('whatsapp-send-message', {
      body: {
        conversation_id: selectedConvId,
        msg_type: type,
        media_url: url,
        client_idempotency_key: newIdempotencyKey(),
        ...(caption ? { media_caption: caption } : {}),
      },
      headers: { Authorization: `Bearer ${session?.access_token ?? ''}` },
    });
    if (error) {
      devLog.error('Error enviando media URL:', error.message);
      setMessages((prev) => prev.map((m) => m.id === id ? { ...m, delivery_status: 'failed' } : m));
    }
  }, [selectedConvId, tenantId, setMessages, messagesEndRef]);

  // ─── Retry de un mensaje failed ───
  const retryMessage = useCallback(async (msgId: string) => {
    const msg = messages.find((m) => m.id === msgId);
    if (!msg || !selectedConvId) return;

    const isMedia = FORWARDABLE_MEDIA_TYPES.has(msg.msg_type);
    const markFailed = () =>
      setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, delivery_status: 'failed' } : m));

    // 🪤 Un media cuyo UPLOAD falló no tiene media_url y su blob local ya se
    // liberó: no hay nada que reenviar. Antes esto no invocaba nada pero igual
    // marcaba 'sent' → el agente veía ✓ y el cliente nunca recibía el archivo.
    if (isMedia && !msg.media_url) {
      toast.error('El archivo no llegó a subirse. Adjuntalo de nuevo para enviarlo.');
      markFailed();
      return;
    }
    if (!isMedia && !msg.body) { markFailed(); return; }

    setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, delivery_status: 'pending' } : m));
    const session = (await supabase.auth.getSession()).data.session;
    // Retry usa NUEVO key — es un envío fresco, no replay del original. El
    // original quedó como 'failed' y queremos mandar uno nuevo a Evolution.
    const retryKey = newIdempotencyKey();
    // El retry de media conservaba solo la URL: se perdían caption y filename.
    const payload = isMedia
      ? {
          conversation_id: selectedConvId,
          msg_type: msg.msg_type,
          media_url: msg.media_url,
          client_idempotency_key: retryKey,
          ...(msg.media_caption ? { media_caption: msg.media_caption } : {}),
          ...(msg.media_filename ? { media_filename: msg.media_filename } : {}),
          ...(msg.is_internal_note ? { is_internal_note: true } : {}),
        }
      : {
          conversation_id: selectedConvId,
          body: msg.body,
          client_idempotency_key: retryKey,
          ...(msg.is_internal_note ? { is_internal_note: true } : {}),
        };
    try {
      const { error } = await supabase.functions.invoke('whatsapp-send-message', {
        body: payload,
        headers: { Authorization: `Bearer ${session?.access_token ?? ''}` },
      });
      if (error) throw error;
      setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, delivery_status: 'sent' } : m));
    } catch {
      markFailed();
    }
  }, [messages, selectedConvId, setMessages, toast]);

  // ─── Programar mensaje para más tarde ───
  const scheduleMessage = useCallback(async (text: string, scheduledAt: string) => {
    if (!selectedConvId || !tenantId || !userId) return;
    const { error } = await supabase.from('wa_scheduled_messages').insert({
      tenant_id: tenantId,
      conversation_id: selectedConvId,
      created_by: userId,
      body: text,
      msg_type: 'text',
      scheduled_at: scheduledAt,
    });
    // El composer ya limpió el input y cerró el popover antes de saber el
    // resultado: sin feedback, un fallo se veía EXACTAMENTE igual que un éxito
    // (el agente creía que el recordatorio quedó programado y nunca existió).
    if (error) {
      logError('wa_schedule_message_failed', new Error(error.message));
      toast.error('No se pudo programar el mensaje. Intentá de nuevo.');
      return;
    }
    toast.success('Mensaje programado');
  }, [selectedConvId, tenantId, userId, toast]);

  // ─── Forward a otra conversación ───
  const forwardMessage = useCallback(async (targetConvId: string, msg: WaMessage) => {
    const session = (await supabase.auth.getSession()).data.session;
    const body: Record<string, string> = {
      conversation_id: targetConvId,
      client_idempotency_key: newIdempotencyKey(),
    };

    if (FORWARDABLE_MEDIA_TYPES.has(msg.msg_type)) {
      // El media es LAZY (se materializa en R2 on-demand): el historial antiguo
      // llega con media_url=null. Antes se hacía `return` en silencio y el
      // usuario veía llegar solo el texto, sin ningún error. Ahora resolvemos la
      // URL con wa-fetch-media (el mismo camino que usa el visor) y recién ahí
      // reenviamos.
      let url = msg.media_url ?? null;
      if (!url && session?.access_token) {
        url = await resolveMediaUrl(msg.id, session.access_token);
      }
      if (!url) {
        // Sin URL no hay imagen que reenviar: NO caer al envío de solo texto
        // (eso es justamente lo que hacía parecer que "se perdió la imagen").
        // Se avisa al usuario: antes esto era un `return` mudo.
        logError(
          `wa_forward_media_unresolved msg=${msg.id} type=${msg.msg_type} target=${targetConvId}`,
          new Error('media_url no disponible'),
        );
        toast.error('No se pudo obtener el archivo para reenviar. Abrí el mensaje y volvé a intentar.');
        return;
      }
      body.media_url = url;
      // Evolution no acepta 'voice' ni 'sticker' como mediatype en sendMedia
      // (el audio va por su propio endpoint). Se mapean al tipo equivalente,
      // si no el reenvío fallaba del lado del provider.
      body.msg_type = msg.msg_type === 'voice' ? 'audio'
        : msg.msg_type === 'sticker' ? 'image'
        : msg.msg_type;
      // El texto de una imagen vive en media_caption; `body` queda por defensa.
      const caption = msg.media_caption || msg.body;
      if (caption) body.media_caption = caption;
    } else if (msg.body) {
      body.body = msg.body;
      body.msg_type = 'text';
    } else {
      return;
    }
    await supabase.functions.invoke('whatsapp-send-message', {
      body,
      headers: { Authorization: `Bearer ${session?.access_token ?? ''}` },
    });
    if (tenantId && userId) {
      void waAuditLog(tenantId, selectedConvId, userId, 'forward', {
        target_conversation: targetConvId, msg_type: msg.msg_type,
      });
    }
    onForwardComplete?.();
  }, [selectedConvId, tenantId, userId, onForwardComplete, toast]);

  return {
    sendText,
    sendMedia,
    sendMediaUrl,
    retryMessage,
    scheduleMessage,
    forwardMessage,
  };
}
